import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

/** Playout engine adapter (ffmpeg-nvenc / gstreamer / srt / ndi / aja-sdi). */
export default function EngineAdapterPanel({ engine, setEngine, engineAdapters, engineHello, engineStatus }) {
  return (
    <>
          {/* Playout engine adapter */}
          <div className="border-t border-zinc-800 pt-4" data-testid="engine-section">
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <div className="font-display font-bold">Playout engine · adapter</div>
                {engineHello && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500" data-testid="engine-protocol">
                    stream · {engineHello.protocol}
                  </div>
                )}
              </div>
              {engineStatus && (
                <div className="flex items-center gap-1">
                  <span
                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm ${
                      engineStatus.health === "healthy"
                        ? "text-emerald-400 bg-emerald-500/10 border border-emerald-500/40"
                        : "text-amber-400 bg-amber-500/10 border border-amber-500/40"
                    }`}
                    data-testid="engine-status-chip"
                  >
                    ● {engineStatus.health} · GPU {engineStatus.gpu_utilization_pct}% · {engineStatus.encoder_fps} fps · {engineStatus.encoder_latency_ms} ms
                  </span>
                  <span className="font-mono text-[10px] text-zinc-500" data-testid="engine-seq">
                    seq {engineStatus.seq}
                  </span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Adapter</Label>
                <select
                  value={engine.adapter}
                  onChange={(e) => setEngine({ ...engine, adapter: e.target.value })}
                  className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                  data-testid="engine-adapter"
                >
                  {engineAdapters.map((a) => (
                    <option key={a.adapter} value={a.adapter}>{a.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Encoder codec</Label>
                <select
                  value={engine.encoder_codec}
                  onChange={(e) => setEngine({ ...engine, encoder_codec: e.target.value })}
                  className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                  data-testid="engine-codec"
                >
                  <option value="H.264">H.264 / AVC</option>
                  <option value="H.265">H.265 / HEVC</option>
                  <option value="AV1">AV1</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Preset</Label>
                <select
                  value={engine.encoder_preset}
                  onChange={(e) => setEngine({ ...engine, encoder_preset: e.target.value })}
                  className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                >
                  <option value="ll">ll · low-latency</option>
                  <option value="llhq">llhq · low-latency HQ</option>
                  <option value="llhp">llhp · low-latency HP</option>
                  <option value="medium">medium</option>
                  <option value="slow">slow</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Keyframe (sec)</Label>
                <Input
                  type="number" min="1" value={engine.keyframe_interval_sec}
                  onChange={(e) => setEngine({ ...engine, keyframe_interval_sec: Number(e.target.value) })}
                  className="mt-1 bg-black border-zinc-800 font-mono text-xs"
                  data-testid="engine-keyframe"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Bitrate ladder (kbps · comma-separated)</Label>
                <Input
                  value={engine.bitrate_ladder_kbps.join(", ")}
                  onChange={(e) =>
                    setEngine({
                      ...engine,
                      bitrate_ladder_kbps: e.target.value
                        .split(",")
                        .map((s) => Number(s.trim()))
                        .filter((n) => n > 0),
                    })
                  }
                  className="mt-1 bg-black border-zinc-800 font-mono text-xs"
                  data-testid="engine-ladder"
                  placeholder="800, 1600, 3200, 6000"
                />
              </div>
              <div className="col-span-2 flex items-center gap-3">
                <label className="flex items-center gap-1 text-xs text-zinc-300">
                  <input
                    type="checkbox"
                    checked={engine.scte35_passthrough}
                    onChange={(e) => setEngine({ ...engine, scte35_passthrough: e.target.checked })}
                    className="accent-red-500"
                  />
                  SCTE-35 passthrough
                </label>
                <label className="flex items-center gap-1 text-xs text-zinc-300">
                  <input
                    type="checkbox"
                    checked={engine.low_latency_mode}
                    onChange={(e) => setEngine({ ...engine, low_latency_mode: e.target.checked })}
                    className="accent-red-500"
                  />
                  Low-latency mode
                </label>
              </div>
              {(engineHello || engineStatus) && (
                <div className="col-span-2 border border-zinc-800 rounded-sm bg-black/40 p-2 font-mono text-[10px] text-zinc-400 grid grid-cols-2 gap-x-4">
                  <div>node <span className="text-zinc-100">{engineHello?.node_id || engineStatus?.node_id || "—"}</span></div>
                  <div>endpoint <span className="text-zinc-100">{engineHello?.endpoint || "—"}</span></div>
                  {engineStatus && (
                    <>
                      <div>GPU mem <span className="text-emerald-400">{engineStatus.gpu_memory_mb} MB</span></div>
                      <div>CPU <span className="text-emerald-400">{engineStatus.cpu_pct}%</span></div>
                      <div>Thermal <span className="text-emerald-400">{engineStatus.thermal_c}°C</span></div>
                      <div>Dropped frames <span className={engineStatus.dropped_frames > 0 ? "text-red-400" : "text-emerald-400"}>{engineStatus.dropped_frames}</span></div>
                      <div>Ingress <span className="text-emerald-400">{engineStatus.input_kbps} kbps</span></div>
                      <div>Egress ladder <span className="text-emerald-400">{engineStatus.output_kbps} kbps</span></div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

    </>
  );
}
